/**
 * Calendar-month keys for the dashboard period selector, as `YYYY-MM`.
 *
 * Dates are built as strings from local calendar fields, never through
 * `toISOString()`: that converts to UTC, and during British Summer Time local
 * midnight is 23:00 the previous day, which silently dropped the last day of
 * every March–September month from the historical view.
 */

/** `YYYY-MM` for the local calendar month containing `date`. */
export function monthKey(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` for the local calendar day of `date` — the value for a ledger DATE. */
export function localDate(date: Date): string {
    return `${monthKey(date)}-${String(date.getDate()).padStart(2, '0')}`;
}

/** `YYYY-MM` of the month before `key`. */
export function previousMonthKey(key: string): string {
    const [year, month] = key.split('-').map(Number);
    return monthKey(new Date(year, month - 2, 1));
}

/** "Oct 2026" for a `YYYY-MM` key. */
export function monthLabel(key: string): string {
    const [year, month] = key.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
}

/** First and last day (inclusive, `YYYY-MM-DD`) of a `YYYY-MM` month. */
export function monthRange(key: string): { start: string; end: string } {
    const [year, month] = key.split('-').map(Number);
    // Day 0 of the next month is the last day of this one.
    const lastDay = new Date(year, month, 0).getDate();
    const mm = String(month).padStart(2, '0');
    return {
        start: `${year}-${mm}-01`,
        end: `${year}-${mm}-${String(lastDay).padStart(2, '0')}`,
    };
}
